import { Link, useParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';
import { getProjectGalleryUrls } from '../api/cmsProjects';
import { USE_STATIC_SITE_CONTENT } from '../config/staticSiteMode';
import { getStaticProjectById } from '../data/staticSite/content';
import { PageSeo } from '../components/PageSeo';
import { resolvePublicMediaUrl } from '../config/api';
import { SubpageLayout } from '../components/SubpageLayout';
import { stripHtmlForMeta, truncateMetaDescription } from '../utils/metaText';

function formatDateLong(isoDate) {
    if (!isoDate) return '';
    const d = new Date(isoDate);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('tr-TR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
}

function formatDateRange(startIso, endIso) {
    const start = formatDateLong(startIso);
    if (!start) return '';
    if (!endIso) {
        return `${start} — Devam ediyor`;
    }
    const end = formatDateLong(endIso);
    return end ? `${start} — ${end}` : start;
}

/**
 * @param {number | undefined} status
 */
function projectStatusLabel(status) {
    switch (Number(status)) {
        case 0:
            return 'Planlama';
        case 1:
            return 'Aktif';
        case 2:
            return 'Tamamlandı';
        default:
            return null;
    }
}

/**
 * @param {string} raw
 */
function DescriptionBody({ raw }) {
    if (!raw || !String(raw).trim()) {
        return null;
    }
    const t = String(raw).trim();
    const looksLikeHtml = /<[a-z][\s\S]*>/i.test(t);

    if (looksLikeHtml) {
        return (
            <div
                className="news-detail-body-html project-detail-description"
                dangerouslySetInnerHTML={{ __html: t }}
            />
        );
    }

    const blocks = t.split(/\n{2,}/);
    return (
        <div className="news-detail-body-text project-detail-description">
            {blocks.map((block, i) => (
                <p key={i} className="news-detail-body-p mb-0">
                    {block.split('\n').map((line, j) => (
                        <span key={j}>
                            {j > 0 ? <br /> : null}
                            {line}
                        </span>
                    ))}
                </p>
            ))}
        </div>
    );
}

/**
 * @param {{ name: string, imageUrl: string }} props
 */
function MemberAvatar({ name, imageUrl }) {
    const [failed, setFailed] = useState(false);
    const initials = useMemo(() => {
        if (!name || !name.trim()) return '?';
        const p = name.trim().split(/\s+/);
        if (p.length >= 2) {
            return (
                (p[0][0] + p[p.length - 1][0]).toUpperCase()
            );
        }
        return name.substring(0, 2).toUpperCase();
    }, [name]);

    if (failed || !imageUrl) {
        return (
            <div
                className="project-detail-member-fallback d-flex align-items-center justify-content-center rounded-circle mx-auto"
                aria-hidden
            >
                {initials}
            </div>
        );
    }

    return (
        <img
            src={imageUrl}
            alt=""
            className="project-detail-member-img rounded-circle mx-auto d-block"
            loading="lazy"
            onError={() => setFailed(true)}
        />
    );
}

/**
 * Proje detayı — URL: /projects/:projectId
 * API: GET /api/cms/Project/GetById?id=
 */
export function ProjectDetailPage() {
    const { t } = useTranslation();
    const { projectId } = useParams();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(!USE_STATIC_SITE_CONTENT);
    const [error, setError] = useState(null);

    const fancyboxGroup = useMemo(
        () => `project-detail-${projectId || 'gallery'}`,
        [projectId],
    );

    const galleryUrls = useMemo(
        () => (project ? getProjectGalleryUrls(project) : []),
        [project],
    );

    const coverUrl = useMemo(() => {
        if (!project?.coverImageUrl) return null;
        return resolvePublicMediaUrl(project.coverImageUrl);
    }, [project]);

    const sortedMembers = useMemo(() => {
        const list = Array.isArray(project?.groupMembers)
            ? [...project.groupMembers]
            : [];
        return list.sort((a, b) => {
            if (a.isLeader && !b.isLeader) return -1;
            if (!a.isLeader && b.isLeader) return 1;
            return (a.fullName || '').localeCompare(b.fullName || '', 'tr');
        });
    }, [project]);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            if (!projectId) {
                setError('Geçersiz adres');
                setLoading(false);
                setProject(null);
                return;
            }

            setLoading(true);
            setError(null);
            setProject(null);

            if (USE_STATIC_SITE_CONTENT) {
                const data = getStaticProjectById(projectId);
                if (cancelled) return;
                if (!data) {
                    setError('Proje bulunamadı');
                    setProject(null);
                } else {
                    setProject(data);
                }
                setLoading(false);
                return;
            }

            /*
            try {
                const data = await fetchCmsProjectById(projectId);
                if (cancelled) return;
                setProject(data);
            } catch (e) {
                if (cancelled) return;
                setError(
                    e instanceof Error ? e.message : 'Proje yüklenemedi',
                );
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
            */
        })();

        return () => {
            cancelled = true;
        };
    }, [projectId]);

    useEffect(() => {
        if (!coverUrl && galleryUrls.length === 0) return undefined;

        const escapedGroup = CSS.escape(fancyboxGroup);
        const selector = `[data-fancybox="${escapedGroup}"]`;

        Fancybox.bind(selector, {
            Carousel: {
                infinite: false,
            },
            compact: false,
            placeFocusBack: true,
        });

        return () => {
            Fancybox.close();
            Fancybox.unbind(selector);
        };
    }, [coverUrl, galleryUrls, fancyboxGroup]);

    if (loading) {
        return (
            <>
                <PageSeo
                    title={`${t('projectsList.title')} — ${t('header.rotaryAlt')}`}
                    description={t('seo.projectsList')}
                />
                <SubpageLayout
                    title="Projelerimiz"
                    subtitle="Yükleniyor…"
                    contentClassName="project-detail-page"
                >
                    <p
                        className="text-center text-secondary mb-0 py-5"
                        role="status"
                        aria-live="polite"
                    >
                        Proje yükleniyor…
                    </p>
                </SubpageLayout>
            </>
        );
    }

    if (error || !project) {
        return (
            <>
                <PageSeo
                    title={`${t('projectsList.title')} — ${t('header.rotaryAlt')}`}
                    description={t('seo.projectFallback')}
                    noindex
                />
                <SubpageLayout
                    title="Proje bulunamadı"
                    subtitle={
                        error ||
                        'Aradığınız proje mevcut değil veya kaldırılmış olabilir.'
                    }
                    contentClassName="project-detail-page"
                >
                    <p className="mb-4">
                        <Link
                            to="/projects"
                            className="news-detail-back-link d-inline-flex align-items-center gap-2 text-decoration-none"
                        >
                            <ChevronLeft size={20} strokeWidth={2} aria-hidden />
                            Projeler listesine dön
                        </Link>
                    </p>
                </SubpageLayout>
            </>
        );
    }

    const statusText = projectStatusLabel(project.status);
    const dateRange = formatDateRange(project.startDate, project.endDate);
    const createdLabel = formatDateLong(project.createdAtUtc);
    const projectMetaDesc = truncateMetaDescription(
        stripHtmlForMeta(project.description || project.purpose || '') ||
            t('seo.projectFallback'),
    );

    return (
        <>
            <PageSeo
                title={`${project.name} — Projeler`}
                description={projectMetaDesc}
                ogImage={coverUrl || undefined}
            />
            <SubpageLayout
                title={project.name}
                subtitle={project.theme || undefined}
                contentClassName="project-detail-page"
            >
            <nav className="mb-4" aria-label="İç sayfa">
                <Link
                    to="/projects"
                    className="news-detail-back-link d-inline-flex align-items-center gap-2 text-decoration-none"
                >
                    <ChevronLeft size={20} strokeWidth={2} aria-hidden />
                    Projelerimize Dön
                </Link>
            </nav>

            {coverUrl ? (
                <div className="news-detail-hero rounded-4 mb-4">
                    <a
                        href={coverUrl}
                        className="news-detail-hero-link"
                        data-fancybox={fancyboxGroup}
                        data-caption={project.name}
                        aria-label={`${project.name} — büyük görsel`}
                    >
                        <img
                            src={coverUrl}
                            alt=""
                            className="news-detail-hero-img"
                        />
                    </a>
                </div>
            ) : null}

            <div className="project-detail-meta rounded-4 p-3 p-md-4 mb-4">
                <div className="row g-3 g-md-4">
                    {project.purpose ? (
                        <div className="col-12 col-md-6">
                            <p className="project-detail-meta-label mb-1">
                                Amaç
                            </p>
                            <p className="project-detail-meta-value mb-0">
                                {project.purpose}
                            </p>
                        </div>
                    ) : null}
                    {dateRange ? (
                        <div className="col-12 col-md-6">
                            <p className="project-detail-meta-label mb-1">
                                Tarih
                            </p>
                            <p className="project-detail-meta-value mb-0">
                                {dateRange}
                            </p>
                        </div>
                    ) : null}
                    {statusText ? (
                        <div className="col-12 col-md-6">
                            <p className="project-detail-meta-label mb-1">
                                Durum
                            </p>
                            <span className="project-detail-status-badge">
                                {statusText}
                            </span>
                        </div>
                    ) : null}
                    {createdLabel ? (
                        <div className="col-12 col-md-6">
                            <p className="project-detail-meta-label mb-1">
                                Kayıt
                            </p>
                            <p className="project-detail-meta-value mb-0">
                                {createdLabel}
                            </p>
                        </div>
                    ) : null}
                </div>
            </div>

            {project.description ? (
                <section className="mb-4" aria-labelledby="project-desc-heading">
                    <h2
                        id="project-desc-heading"
                        className="project-detail-section-title h5 mb-3"
                    >
                        Proje Hakkında
                    </h2>
                    <DescriptionBody raw={project.description} />
                </section>
            ) : null}

            {sortedMembers.length > 0 ? (
                <section className="mb-4" aria-labelledby="project-team-heading">
                    <h2
                        id="project-team-heading"
                        className="project-detail-section-title h5 mb-3"
                    >
                        Ekip
                    </h2>
                    <ul className="list-unstyled row row-cols-2 row-cols-md-3 row-cols-lg-4 g-3 mb-0">
                        {sortedMembers.map((m) => {
                            const pic = m.profilePictureUrl
                                ? resolvePublicMediaUrl(m.profilePictureUrl)
                                : '';
                            const memberId =
                                m.id != null && String(m.id).trim()
                                    ? String(m.id).trim()
                                    : '';
                            const label = m.fullName
                                ? `${m.fullName} — üye profili`
                                : 'Üye profili';
                            const cardClass =
                                'project-detail-member h-100 rounded-4 p-3 text-center';
                            const body = (
                                <>
                                    <MemberAvatar
                                        name={m.fullName || ''}
                                        imageUrl={pic}
                                    />
                                    <p className="project-detail-member-name mb-1 mt-2">
                                        {m.fullName}
                                    </p>
                                    {m.isLeader ? (
                                        <span className="project-detail-leader-badge">
                                            Lider
                                        </span>
                                    ) : null}
                                </>
                            );
                            return (
                                <li key={m.id || m.fullName} className="col">
                                    {memberId ? (
                                        <Link
                                            to={`/member-area/members/${encodeURIComponent(memberId)}`}
                                            className={`${cardClass} project-detail-member-link d-block text-decoration-none`}
                                            aria-label={label}
                                        >
                                            {body}
                                        </Link>
                                    ) : (
                                        <div className={cardClass}>{body}</div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </section>
            ) : null}

            {galleryUrls.length > 0 ? (
                <section className="project-detail-gallery" aria-label="Galeri">
                    <h2 className="news-detail-gallery-title h5 mb-3">
                        Galeri
                    </h2>
                    <ul className="news-detail-gallery-grid list-unstyled row row-cols-2 row-cols-lg-4 g-3 mb-0">
                        {galleryUrls.map((url) => (
                            <li key={url} className="col">
                                <a
                                    href={url}
                                    className="news-detail-gallery-link"
                                    data-fancybox={fancyboxGroup}
                                    data-caption={project.name}
                                >
                                    <img
                                        src={url}
                                        alt=""
                                        className="news-detail-gallery-thumb"
                                        loading="lazy"
                                    />
                                </a>
                            </li>
                        ))}
                    </ul>
                </section>
            ) : null}
        </SubpageLayout>
        </>
    );
}
